import { z } from "zod";
import {
  CONTACT_GUEST_RANGES,
  DEFAULT_GUEST_BOUNDS,
  guestRangeMax,
  guestRangeMin,
  type ContactGuestRange,
} from "./schemas";
import { PLAN_LIMITS, type PlanId } from "./constants";

/**
 * Admin'in "Satış & Site" panelinden yönettiği landing içeriği.
 *
 * Depolama: D1 `platform_settings` (anahtar–değer, migration 013) — her bölüm
 * `site:<bölüm>` anahtarında JSON. Okuma ASLA kırılmaz: kayıt yoksa, JSON bozuksa
 * ya da şema değiştiyse bölüm varsayılana düşer (landing her koşulda çizilir).
 *
 * Yeni bölüm eklemek: şema + `SITE_SECTION_SCHEMAS` + `SITE_DEFAULTS` satırı.
 * Plan: docs/plans/2026-10-13-satis-site-yonetimi.md
 */

const planIdSchema = z.enum(["free", "pro", "max"]);

/** Bir misafir aralığının fiyatı. `price: null` → o aralık "teklif ile". */
const pricingTierSchema = z.object({
  price: z.number().int().min(0).max(1_000_000).nullable(),
  /** "₺4.990'dan" — kesin değil, teklifle netleşir. */
  from: z.boolean(),
  /** Admin'in atayacağı plan (satış notu; otomatik atama yok). */
  plan: planIdSchema,
});
export type PricingTier = z.infer<typeof pricingTierSchema>;

const tiersSchema = z.object(
  Object.fromEntries(CONTACT_GUEST_RANGES.map((r) => [r, pricingTierSchema])) as Record<
    ContactGuestRange,
    typeof pricingTierSchema
  >,
);

/**
 * Kademe sınırları (4 artan tam sayı): [50, 150, 300, 500] → "50'ye kadar",
 * "50–150", "150–300", "300–500", "500+". Admin değiştirir; satılmış siparişin
 * hakkı sipariş anında kopyalandığı için ETKİLENMEZ.
 */
export const guestBoundsSchema = z
  .array(z.number().int().min(1).max(100_000))
  .length(CONTACT_GUEST_RANGES.length - 1)
  .refine((b) => b.every((v, i) => i === 0 || v > b[i - 1]), { message: "bounds_order" });

export const sitePricingSchema = z.object({
  /** Fiyatlar sitede görünsün mü (kapalıysa plan kartları + formda tahmin yok). */
  show: z.boolean(),
  /** İsteğe bağlı serbest not; boşsa landing kendi (çevrilmiş) metnini kullanır. */
  note: z.string().max(200),
  /**
   * ESKİ (2026-10-16'ya kadar): misafir aralığına göre fiyat kademeleri. Artık satışta, kartta ve
   * formda KULLANILMAZ — paketin fiyatı ve tüm sayıları paket tanımından (`plans.price_try`,
   * `SitePlanCard.price`). Kayıtlı eski JSON bozulmasın diye şemada kalır; eski talep/siparişlerin
   * aralık etiketleri için okunur.
   */
  tiers: tiersSchema,
  bounds: guestBoundsSchema,
});
export type SitePricing = z.infer<typeof sitePricingSchema>;

/**
 * Çift yorumu — YALNIZ gerçek ve izni alınmış yorumlar (uydurma yorum Ticari
 * Reklam Yönetmeliği'ne aykırı). Varsayılan liste BOŞ → landing bölümü gizli.
 * Metin girildiği dilde gösterilir (çevrilmez).
 */
const testimonialSchema = z.object({
  id: z.string().min(1).max(40),
  couple: z.string().trim().min(1).max(80),
  /** Serbest metin: "Haziran 2026", "Kına + Düğün · 2026". */
  when: z.string().trim().max(40),
  city: z.string().trim().max(40),
  quote: z.string().trim().min(1).max(400),
  rating: z.number().int().min(1).max(5).nullable(),
  visible: z.boolean(),
});
export type Testimonial = z.infer<typeof testimonialSchema>;
export const TESTIMONIALS_MAX = 12;
export const siteTestimonialsSchema = z.object({ items: z.array(testimonialSchema).max(TESTIMONIALS_MAX) });
export type SiteTestimonials = z.infer<typeof siteTestimonialsSchema>;

/**
 * Gerçek kullanım sayıları (D1'den) — eşik altındaysa landing'e HİÇ gitmez
 * (küçük bir sayı güven değil şüphe yaratır; uydurma sayı asla yok).
 */
export const siteStatsSchema = z.object({
  show: z.boolean(),
  /** "Düğün" = en az `PROOF_MIN_MEDIA_PER_WEDDING` onaylı karesi olan düğün. */
  minWeddings: z.number().int().min(1).max(100_000),
  minMedia: z.number().int().min(1).max(100_000_000),
});
export type SiteStats = z.infer<typeof siteStatsSchema>;

/**
 * Vitrin medyası — landing'deki stok (Unsplash) görsellerin yerine gerçek
 * düğün kareleri + Sahne bölümünde gerçek ekran videosu. Değerler R2
 * anahtarıdır (`landing/...`), URL değil; landing `/cdn/<anahtar>` ile çizer.
 * Yükleme/silme admin uçlarından (`/api/admin/site/media/*`), R2 ile senkron.
 */
export const LANDING_MEDIA_KEY_RE = /^landing\/[a-z0-9-]+\.(jpg|png|webp|avif|mp4|webm|mov)$/;
const landingKey = z.string().regex(LANDING_MEDIA_KEY_RE);
export const LANDING_PHOTOS_MAX = 12;
export const siteMediaSchema = z.object({
  photos: z.array(landingKey).max(LANDING_PHOTOS_MAX),
  sahneVideo: landingKey.nullable(),
  sahnePoster: landingKey.nullable(),
});
export type SiteMedia = z.infer<typeof siteMediaSchema>;

/**
 * Saklama vaadi — landing SSS'sinde "Albüm ne kadar açık kalır?" + plan notu.
 * Kod onaylı medyayı OTOMATİK SİLMEZ (yalnız silinmiş/reddedilmiş kareler
 * 7 gün sonra temizlenir); vaat bu davranışın ALT sınırıdır, üstü değil.
 */
export const siteRetentionSchema = z.object({
  show: z.boolean(),
  months: z.number().int().min(1).max(120),
});
export type SiteRetention = z.infer<typeof siteRetentionSchema>;

/**
 * Organizatör kendi kendine kayıt (F8; 2026-10-15'te yeniden). Varsayılan
 * KAPALI: yeni hesabı admin açar (teklif → satış). Açıkken bile giriş isteği
 * hesap AÇMAZ — yalnız organizatör app'teki `/signup` akışı (ad + koşul onayı →
 * e-posta kodu doğrulanınca hesap). Mevcut hesaplar hiçbir koşulda etkilenmez.
 */
export const siteSignupSchema = z.object({
  selfServe: z.boolean(),
  /**
   * Online satış (F8b): landing'de "Satın al" + `/checkout`. Varsayılan KAPALI;
   * açık olsa bile fiyatlar gizliyse ya da yasal kimlik eksikse görünmez
   * (`salesOpen`). Ödeme şimdilik elle (havale/EFT) — admin "ödendi" işaretler.
   */
  orders: z.boolean(),
});
export type SiteSignup = z.infer<typeof siteSignupSchema>;

/**
 * Ek paketler (2026-10-15) — "QR ile ekstra kayıt": düğünün paket misafir limiti
 * dolunca QR'dan gelen ekstra kişi için organizatörün açtığı ücretli paketler.
 * Fiyat her zaman SUNUCU ayarından (istemciye güvenilmez). `enabled: false` →
 * organizatör yeni paket açamaz (mevcutlar etkilenmez).
 */
export const qrExtraPackSchema = z.object({
  seats: z.number().int().min(1).max(1000),
  price: z.number().int().min(0).max(100_000),
});
export type QrExtraPack = z.infer<typeof qrExtraPackSchema>;

export const siteAddonsSchema = z.object({
  qrExtra: z.object({
    enabled: z.boolean(),
    packs: z
      .array(qrExtraPackSchema)
      .min(1)
      .max(5)
      .refine((p) => new Set(p.map((x) => x.seats)).size === p.length, "duplicate seats"),
  }),
});
export type SiteAddons = z.infer<typeof siteAddonsSchema>;

/**
 * Veri sorumlusu / satıcı kimliği (F9). KVKK m.10/a ve Mesafeli Sözleşmeler
 * Yönetmeliği bu bilgilerin AÇIKÇA gösterilmesini ister → bunlar bilerek
 * PUBLIC'tir. Varsayılan BOŞ (kullanıcı kararı 2026-10-12: kişisel bilgiler
 * askıda); boşken KVKK sayfası yalnız iletişim kanalını gösterir ve satın
 * alma akışı (F8b) kapalı kalır.
 */
const legalText = (max: number) => z.string().trim().max(max);
export const siteLegalSchema = z.object({
  /** Ad soyad ya da ticari unvan. */
  name: legalText(160),
  /** Ör. "Şahıs işletmesi", "Yazılım geliştirme". */
  title: legalText(120),
  address: legalText(300),
  phone: legalText(30),
  email: legalText(120),
  taxOffice: legalText(80),
  /** VKN / TCKN. */
  taxId: legalText(20),
  mersis: legalText(30),
  kep: legalText(120),
});
export type SiteLegal = z.infer<typeof siteLegalSchema>;

/** Kimlik yayına yetecek kadar dolu mu? (ad + adres + e-posta) */
export function legalIdentityComplete(legal: SiteLegal): boolean {
  return Boolean(legal.name && legal.address && legal.email);
}

/** Canlı demo CTA'ları — admin demo düğününü kurup açınca landing'de görünür. */
export const siteDemoSchema = z.object({ show: z.boolean() });
export type SiteDemo = z.infer<typeof siteDemoSchema>;

export const SITE_SECTION_SCHEMAS = {
  pricing: sitePricingSchema,
  testimonials: siteTestimonialsSchema,
  stats: siteStatsSchema,
  media: siteMediaSchema,
  retention: siteRetentionSchema,
  demo: siteDemoSchema,
  signup: siteSignupSchema,
  legal: siteLegalSchema,
  addons: siteAddonsSchema,
} as const;

export type SiteSection = keyof typeof SITE_SECTION_SCHEMAS;
export const SITE_SECTIONS = Object.keys(SITE_SECTION_SCHEMAS) as SiteSection[];
export type SiteSettings = { [K in SiteSection]: z.infer<(typeof SITE_SECTION_SCHEMAS)[K]> };

/**
 * Varsayılanlar — fiyat gerekçesi plan dosyasında (TR pazarı ₺899–3.990 tek
 * seferlik; Sahne + SMS doğrulama ile üst-orta bant; değişken maliyet misafir
 * başına SMS doğrulaması → misafir aralığına göre kademe). Üstü çizili "eski
 * fiyat" bilerek yok (Ticari Reklam Yönetmeliği).
 */
export const SITE_DEFAULTS: SiteSettings = {
  pricing: {
    show: true,
    note: "",
    tiers: {
      lt50: { price: 1490, from: false, plan: "pro" },
      "50_150": { price: 2490, from: false, plan: "pro" },
      "150_300": { price: 2990, from: false, plan: "pro" },
      "300_500": { price: 3990, from: false, plan: "max" },
      gt500: { price: 4990, from: true, plan: "max" },
    },
    bounds: [...DEFAULT_GUEST_BOUNDS],
  },
  testimonials: { items: [] },
  stats: { show: true, minWeddings: 10, minMedia: 1000 },
  media: { photos: [], sahneVideo: null, sahnePoster: null },
  retention: { show: true, months: 12 },
  demo: { show: false },
  signup: { selfServe: false, orders: false },
  legal: { name: "", title: "", address: "", phone: "", email: "", taxOffice: "", taxId: "", mersis: "", kep: "" },
  // Kullanıcı kararı 2026-10-15: +20 ₺290 · +50 ₺590 · +100 ₺990 (admin değiştirir).
  addons: {
    qrExtra: {
      enabled: true,
      packs: [
        { seats: 20, price: 290 },
        { seats: 50, price: 590 },
        { seats: 100, price: 990 },
      ],
    },
  },
};

export const siteSettingsKey = (section: SiteSection): string => `site:${section}`;

export function isSiteSection(s: string): s is SiteSection {
  return Object.prototype.hasOwnProperty.call(SITE_SECTION_SCHEMAS, s);
}

/**
 * Ham değeri (JSON string ya da nesne) bölüm şemasına göre çözer. Kayıtlı
 * alanlar varsayılanın üstüne yazılır (yeni eklenen alanlar eski kayıtlarda
 * varsayılandan gelir); sonuç şemadan geçmezse tamamen varsayılan.
 */
export function parseSiteSection<K extends SiteSection>(section: K, raw: unknown): SiteSettings[K] {
  const defaults = SITE_DEFAULTS[section];
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return defaults;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return defaults;
  const res = SITE_SECTION_SCHEMAS[section].safeParse({ ...defaults, ...(value as object) });
  return res.success ? (res.data as SiteSettings[K]) : defaults;
}

/** `platform_settings` satırlarından tüm bölümler (tanınmayan anahtarlar yok sayılır). */
export function buildSiteSettings(rows: ReadonlyArray<{ key: string; value: string }>): SiteSettings {
  const out = { ...SITE_DEFAULTS } as SiteSettings;
  for (const { key, value } of rows) {
    if (!key.startsWith("site:")) continue;
    const section = key.slice("site:".length);
    if (!isSiteSection(section)) continue;
    (out as Record<SiteSection, unknown>)[section] = parseSiteSection(section, value);
  }
  return out;
}

/**
 * Paketin sitede gösterilen fiyatı — paket tanımından (`plans.price_try`). Fiyatlar gizliyse
 * ya da paketin fiyatı yoksa (teklifle) `null`. Free (varsayılan paket) ₺0.
 */
export function planPrice(pricing: Pick<SitePricing, "show">, plans: SitePlans, plan: PlanId): number | null {
  if (!pricing.show) return null;
  const price = plans[plan]?.price;
  return typeof price === "number" ? price : null;
}

/**
 * Teklif formundaki seçeneğe göre tahmin: seçilen paketin fiyatı (paket tanımından).
 * "Daha büyük / özel" seçimde ya da fiyatlar gizliyse `null` (teklifle netleşir).
 */
export function estimateForPlan(pricing: Pick<SitePricing, "show">, plans: SitePlans, plan: PlanId | null | undefined): number | null {
  if (!plan || plan === "free") return null;
  return planPrice(pricing, plans, plan);
}

/** ₺ tutarı dile göre: tr "₺2.990", en "₺2,990", fr "2 990 ₺" (kuruş yok). */
export function formatTry(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "TRY",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Türkçe ayrılma hâli eki — "₺1.490’dan", "₺2.000’den", "₺1.500’den".
 * Ek, sayının OKUNUŞUNDAKİ son kelimeye göre seçilir (ünlü uyumu + sert
 * ünsüz benzeşmesi); admin istediği fiyatı girebildiği için sabit yazılamaz.
 */
export function trAblativeSuffix(n: number): "dan" | "den" | "tan" | "ten" {
  const v = Math.abs(Math.trunc(n));
  const ONES = ["dan", "den", "den", "ten", "ten", "ten", "dan", "den", "den", "dan"] as const; // sıfır, bir, iki, üç, dört, beş, altı, yedi, sekiz, dokuz
  const TENS = ["", "dan", "den", "dan", "tan", "den", "tan", "ten", "den", "dan"] as const; // on, yirmi, otuz, kırk, elli, altmış, yetmiş, seksen, doksan
  if (v === 0) return "dan";
  if (v % 10 !== 0) return ONES[v % 10];
  if (v % 100 !== 0) return TENS[(v % 100) / 10] as "dan" | "den" | "tan" | "ten";
  if (v % 1000 !== 0) return "den"; // yüz
  if (v % 1_000_000 !== 0) return "den"; // bin
  return "dan"; // milyon / milyar
}

/** Sayının yönelme eki: 50'ye, 300'e, 20'ye, 1.000'e, 60'a (son okunan kelimeye göre). */
export function trDativeSuffix(n: number): "a" | "e" | "ya" | "ye" {
  const v = Math.abs(Math.trunc(n));
  const ONES = ["a", "e", "ye", "e", "e", "e", "ya", "ye", "e", "a"] as const; // sıfıra, bire, ikiye, üçe, dörde, beşe, altıya, yediye, sekize, dokuza
  const TENS = ["", "a", "ye", "a", "a", "ye", "a", "e", "e", "a"] as const; // ona, yirmiye, otuza, kırka, elliye, altmışa, yetmişe, seksene, doksana
  if (v === 0) return "a";
  if (v % 10 !== 0) return ONES[v % 10];
  if (v % 100 !== 0) return TENS[(v % 100) / 10] as "a" | "e" | "ye";
  if (v % 1000 !== 0) return "e"; // yüze
  if (v % 1_000_000 !== 0) return "e"; // bine
  return "a"; // milyona
}

/** Kademe etiketi parçaları — sayılar admin'in sınırlarından. */
export type GuestRangeParts =
  | { kind: "upTo"; n: number }
  | { kind: "between"; min: number; max: number }
  | { kind: "over"; n: number };

export function guestRangeParts(bounds: readonly number[] | null | undefined, range: ContactGuestRange): GuestRangeParts {
  const max = guestRangeMax(bounds, range);
  const min = guestRangeMin(bounds, range);
  if (max === null) return { kind: "over", n: min };
  if (min === 0) return { kind: "upTo", n: max };
  return { kind: "between", min, max };
}

/** Ekrandaki etiket ("50’ye kadar", "50–150", "500+") — `landing.contact.range*` anahtarları. */
export function guestRangeLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  bounds: readonly number[] | null | undefined,
  range: ContactGuestRange,
): string {
  const p = guestRangeParts(bounds, range);
  if (p.kind === "upTo") return t("landing.contact.rangeUpTo", { n: p.n, suffix: trDativeSuffix(p.n) });
  if (p.kind === "between") return t("landing.contact.rangeBetween", { min: p.min, max: p.max });
  return t("landing.contact.rangeOver", { n: p.n });
}

/** Türkçe sabit etiket (işletme e-postaları): "50'ye kadar", "50 – 150", "500+". */
export function guestRangeLabelTr(bounds: readonly number[] | null | undefined, range: ContactGuestRange): string {
  const p = guestRangeParts(bounds, range);
  if (p.kind === "upTo") return `${p.n}'${trDativeSuffix(p.n)} kadar`;
  if (p.kind === "between") return `${p.min} – ${p.max}`;
  return `${p.n}+`;
}

// ─── Sosyal kanıt: gerçek sayılar ────────────────────────────────────────

/** Bir düğünün "gerçekten kullanılmış" sayılması için gereken onaylı kare. */
export const PROOF_MIN_MEDIA_PER_WEDDING = 10;

/** D1'den ham sayılar (eşiksiz) — yalnız admin görür. */
export interface SiteProofRaw {
  weddings: number;
  media: number;
}

/** Landing'e giden sayılar — eşik altı / kapalı → `null` (o sayı çizilmez). */
export interface SiteProof {
  weddings: number | null;
  media: number | null;
}

export function applyProofThresholds(raw: SiteProofRaw | null, stats: SiteStats): SiteProof {
  if (!raw || !stats.show) return { weddings: null, media: null };
  return {
    weddings: raw.weddings >= stats.minWeddings ? raw.weddings : null,
    media: raw.media >= stats.minMedia ? raw.media : null,
  };
}

// ─── Tanıtım sitesindeki paketler ────────────────────────────────────────────
/**
 * Landing YALNIZ bu üç standart paketi gösterir (karar 2026-10-14). Admin'in
 * yarattığı diğer planlar (ör. `enterprise`, kampanya) ÖZELdir: sitede görünmez,
 * organizatöre admin atar / teklifle satılır. Kartlardaki limitler DB
 * `plans` tablosundan canlı okunur — admin Pro'nun limitini değiştirince site
 * de değişir; sabitler (`PLAN_LIMITS`) yalnız DB okunamazsa yedek.
 */
export const SITE_PLAN_IDS = ["free", "pro", "max"] as const satisfies readonly PlanId[];

export function isSitePlan(slug: string): slug is PlanId {
  return (SITE_PLAN_IDS as readonly string[]).includes(slug);
}

/**
 * Sitedeki kartın sayıları — HEPSİ paket tanımından (`plans`): misafir, medya, depolama ve
 * fiyat (2026-10-16: "sayıların hiçbiri uydurma olmamalı"). Ad BİLEREK yok: paket adları
 * (Free/Pro/Max) sabit marka adı — sözleşme metni ve satın alma e-postaları da bunu kullanır.
 */
export interface SitePlanCard {
  guests: number;
  media: number;
  storageBytes: number;
  /** Satış fiyatı (₺). `null` → fiyat tanımlı değil (online satılmaz, teklifle). Free 0. */
  price: number | null;
  /** SMS gönderim turu + SMS hakkı (segment) — sözleşmeye yazılır. Eski worker vermez → yok. */
  reminderSends?: number;
  smsSegments?: number;
}
export type SitePlans = Record<PlanId, SitePlanCard>;

export function defaultSitePlan(id: PlanId): SitePlanCard {
  const l = PLAN_LIMITS[id];
  // DB okunamazsa yedek: fiyat bilinmez (uydurulmaz) → yalnız Free ₺0.
  return {
    guests: l.maxGuestsPerWedding,
    media: l.maxMediaPerWedding,
    storageBytes: l.maxStorageBytes,
    price: id === "free" ? 0 : null,
  };
}

export const SITE_PLANS_DEFAULT: SitePlans = {
  free: defaultSitePlan("free"),
  pro: defaultSitePlan("pro"),
  max: defaultSitePlan("max"),
};

/** Güvenli çözümleme: bozuk/eksik alan → o paketin sabit değeri (kart her koşulda çizilir). */
export function parseSitePlans(raw: unknown): SitePlans {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const pos = (v: unknown, fb: number) => (typeof v === "number" && Number.isSafeInteger(v) && v > 0 ? v : fb);
  const nonNeg = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
  const out = {} as SitePlans;
  for (const id of SITE_PLAN_IDS) {
    const fb = SITE_PLANS_DEFAULT[id];
    const p = src[id] && typeof src[id] === "object" ? (src[id] as Record<string, unknown>) : {};
    out[id] = {
      guests: pos(p.guests, fb.guests),
      media: pos(p.media, fb.media),
      storageBytes: pos(p.storageBytes, fb.storageBytes),
      price: typeof p.price === "number" && Number.isSafeInteger(p.price) && p.price >= 0 ? p.price : p.price === null ? null : fb.price,
      // Yalnız sunucu verdiyse (sözleşme metni sunucununkiyle aynı girdilerden üretilsin).
      ...(nonNeg(p.reminderSends) && nonNeg(p.smsSegments) ? { reminderSends: p.reminderSends, smsSegments: p.smsSegments } : {}),
    };
  }
  return out;
}

/**
 * `GET /api/site` gövdesi: ayarlar (gizli yorumlar çıkarılmış) + eşikten geçmiş
 * sayılar + sitedeki üç paketin DB'den limitleri.
 */
export type PublicSite = SiteSettings & { proof: SiteProof; plans: SitePlans };

export function toPublicSite(
  settings: SiteSettings,
  raw: SiteProofRaw | null,
  plans: SitePlans = SITE_PLANS_DEFAULT,
): PublicSite {
  return {
    ...settings,
    testimonials: { items: settings.testimonials.items.filter((t) => t.visible) },
    proof: applyProofThresholds(raw, settings.stats),
    plans,
  };
}

/** İstemci tarafı: `proof` alanını güvenle çözer (bozuksa iki sayı da `null`). */
export function parseSiteProof(raw: unknown): SiteProof {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const num = (v: unknown) => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);
  return { weddings: num(src.weddings), media: num(src.media) };
}
